import React, { useState, useEffect, useCallback } from 'react';
import { ticketService } from '../services/api';
import { Ticket } from '../types';
import { toast } from 'react-hot-toast';

export const TicketingPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved'>('all');
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    siteId: '', // We keep this as item ID reference for now
    severity: 'medium' as 'low' | 'medium' | 'high' | 'critical',
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await ticketService.getTickets(filter === 'all' ? undefined : filter)).data;
      setTickets(response);
    } catch (error) {
      toast.error('Failed to fetch incident tickets');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await ticketService.createTicket(newTicket);
      toast.success('Incident ticket created successfully');
      fetchTickets();
      setNewTicket({ title: '', description: '', siteId: '', severity: 'medium' });
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          Incident Management
          <button onClick={fetchTickets} className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full transition-colors border border-slate-700" title="Refresh Data">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
        </h1>
        <p className="text-slate-400 text-sm">Report and track ongoing issues with items and equipment.</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl sticky top-6">
            <h2 className="text-xl font-bold text-white mb-6">Create New Incident</h2>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Title</label>
                <input
                  type="text"
                  placeholder="E.g., Component Failure"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Target Item ID</label>
                <input
                  type="text"
                  placeholder="ITM-XXXX"
                  value={newTicket.siteId}
                  onChange={(e) => setNewTicket({ ...newTicket, siteId: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Severity</label>
                <select
                  value={newTicket.severity}
                  onChange={(e) => setNewTicket({ ...newTicket, severity: e.target.value as any })}
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Description</label>
                <textarea
                  placeholder="Describe the incident in detail..."
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
                  rows={4}
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-bold py-3.5 rounded-xl transition shadow-lg shadow-orange-500/20 mt-4"
              >
                Submit Incident
              </button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="mb-6 flex flex-wrap gap-2 border-b border-slate-800 pb-4">
            {['all', 'open', 'in_progress', 'resolved'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status as any)}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition ${
                  filter === status 
                    ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-lg shadow-orange-500/20' 
                    : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                {status.replace('_', ' ').toUpperCase()}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Loading incidents...</div>
            ) : tickets.length === 0 ? (
              <div className="bg-slate-900/50 border border-slate-800 border-dashed rounded-2xl p-12 text-center text-slate-500">
                No incidents found for this filter.
              </div>
            ) : (
              tickets.map((ticket) => (
                <div key={ticket._id} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 hover:border-orange-500/40 transition">
                  <div className="flex flex-col sm:flex-row justify-between items-start mb-3 gap-2">
                    <h3 className="text-lg font-bold text-white leading-tight">{ticket.title}</h3>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${
                        ticket.severity === 'critical' ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' :
                        ticket.severity === 'high' ? 'bg-orange-500/10 text-orange-500 border-orange-500/20' :
                        ticket.severity === 'medium' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                        'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                      }`}>
                        {ticket.severity}
                      </span>
                      <span className="bg-slate-800 border border-slate-700 text-slate-400 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <p className="text-slate-300 text-sm mb-4 leading-relaxed">{ticket.description}</p>
                  <div className="text-xs text-slate-500 font-mono pt-3 border-t border-slate-800">
                    Item ID: <span className="text-orange-400 font-bold">{ticket.siteId}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
